import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { AssessmentTokenDetails, RangeType, RecommendedPath, UIMeeting } from 'types';
import { useUserContext } from './UserProvider';

export interface MeetingContextOptions {
	meeting: UIMeeting | null;
	hasAssessmentEnded: boolean;
	recommendedUserPath?: RecommendedPath | null;
	predictedUserSalary?: RangeType | null;
	ongoingAssessDetails?: AssessmentTokenDetails;
	refetchUserDetails: boolean;
	setRefetchUserDetails: React.Dispatch<React.SetStateAction<boolean>>;
	setMeeting: React.Dispatch<React.SetStateAction<UIMeeting | null>>;
	setOngoingAssessDetails: React.Dispatch<React.SetStateAction<AssessmentTokenDetails | undefined>>;
	setHasAssessmentEnded: React.Dispatch<React.SetStateAction<boolean>>;
	setRecommendedUserPath: React.Dispatch<React.SetStateAction<RecommendedPath | undefined>>;
	setPredictedUserSalary: React.Dispatch<React.SetStateAction<RangeType | undefined>>;
}
export interface MeetingContextProps {
	children: ReactNode;
}

const MeetingContext = createContext<MeetingContextOptions>({
	meeting: null,
	hasAssessmentEnded: false,
	setMeeting: () => {},
	refetchUserDetails: false,
	setRefetchUserDetails: () => {},
	setHasAssessmentEnded: () => {},
	setOngoingAssessDetails: () => {},
	setPredictedUserSalary: () => {},
	setRecommendedUserPath: () => {},
});

export const MeetingContextProvider = ({ children }: MeetingContextProps) => {
	const queryClient = useQueryClient();
	const { user } = useUserContext();

	const [meeting, setMeeting] = useState<UIMeeting | null>(null);
	const [ongoingAssessDetails, setOngoingAssessDetails] = useState<AssessmentTokenDetails>();
	const [hasAssessmentEnded, setHasAssessmentEnded] = useState<boolean>(false);
	const [recommendedUserPath, setRecommendedUserPath] = useState<RecommendedPath>();
	const [predictedUserSalary, setPredictedUserSalary] = useState<RangeType>();
	const [refetchUserDetails, setRefetchUserDetails] = useState(false);

	useEffect(() => {
		const refetch = setInterval(() => {
			if (refetchUserDetails) {
				queryClient.refetchQueries(['verifyCookie']);
				queryClient.refetchQueries(['getPositions']);
				queryClient.refetchQueries(['getMeetings']);
				if (user?.flags?.lastUnseenMeetingId) setRefetchUserDetails(false);
			}
		}, 15000);

		return () => {
			if (!refetchUserDetails) clearInterval(refetch);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refetchUserDetails]);

	return (
		<MeetingContext.Provider
			value={{
				meeting,
				ongoingAssessDetails,
				hasAssessmentEnded,
				predictedUserSalary,
				recommendedUserPath,
				setPredictedUserSalary,
				setRecommendedUserPath,
				setMeeting,
				setOngoingAssessDetails,
				setHasAssessmentEnded,
				refetchUserDetails,
				setRefetchUserDetails,
			}}
		>
			{children}
		</MeetingContext.Provider>
	);
};

export const useMeetingContext = () => {
	const context = useContext(MeetingContext);
	return context;
};
